import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
} from "react";

type WebSource =
  | { uri: string; headers?: Record<string, string>; method?: "GET" | "POST"; body?: string }
  | { html: string; baseUrl?: string };

type WebViewProps = {
  source: WebSource;
  style?: React.CSSProperties;
  allowFullscreen?: boolean;
  allowsInlineMediaPlayback?: boolean;
  javaScriptEnabled?: boolean;
  domStorageEnabled?: boolean;
  originWhitelist?: string[]; // não usado direto, mas mantido p/ compat
  injectedJavaScript?: string; // só funciona com html/srcDoc
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: (e: any) => void;
  onMessage?: (e: { nativeEvent: { data: any } }) => void;
  onNavigationStateChange?: (state: {
    url?: string;
    title?: string | null;
    loading: boolean;
    canGoBack?: boolean;
    canGoForward?: boolean;
  }) => void;
  // compat RNW:
  containerStyle?: React.CSSProperties;
  // qualquer outra prop cai no iframe
  [key: string]: any;
};

export type WebViewHandle = {
  reload: () => void;
  goBack: () => void;
  goForward: () => void;
  postMessage: (data: any, targetOrigin?: string) => void;
  injectJavaScript: (code: string) => void; // só para srcDoc
};

const ALLOW_ATTR_DEFAULT =
  "geolocation; microphone; camera; midi; encrypted-media; clipboard-read; clipboard-write; fullscreen; autoplay; picture-in-picture";

const SANDBOX_DEFAULT =
  "allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation";

const isHtmlSource = (s: WebSource): s is { html: string; baseUrl?: string } =>
  (s as any).html != null;

export const WebView = forwardRef<WebViewHandle, WebViewProps>((props, ref) => {
  const {
    source,
    style,
    containerStyle,
    allowFullscreen = true,
    allowsInlineMediaPlayback = true,
    javaScriptEnabled = true,
    domStorageEnabled = true, // só informativo no web
    injectedJavaScript,
    onLoadStart,
    onLoadEnd,
    onError,
    onMessage,
    onNavigationStateChange,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Monta o srcDoc quando vier HTML bruto (permite inject)
  const srcDoc = useMemo(() => {
    if (!isHtmlSource(source)) return undefined;

    const inject = injectedJavaScript && javaScriptEnabled
      ? `<script>(function(){try{${injectedJavaScript}\n}catch(e){console.error('injectedJavaScript error', e);}})();</script>`
      : "";

    // Bridge simples para postMessage compatível com RN WebView
    const bridge = `
      <script>
        (function() {
          window.ReactNativeWebView = {
            postMessage: function(data) {
              // envia para o parent
              parent.postMessage({ __RN_WEBVIEW_BRIDGE: true, data: String(data) }, '*');
            }
          };
        })();
      </script>
    `;

    // base para assets relativos, se fornecido
    const base = source.baseUrl ? `<base href="${source.baseUrl}">` : "";

    return `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        ${base}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>html,body{margin:0;padding:0;}</style>
      </head>
      <body>
        ${source.html}
        ${bridge}
        ${inject}
      </body>
      </html>
    `;
  }, [source, injectedJavaScript, javaScriptEnabled]);

  useEffect(() => {
    onLoadStart?.();
    // Nota: não temos evento de "start" real no iframe; apenas chamamos no mount/update.
    // onLoadEnd será disparado no evento "load" do iframe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcDoc, (source as any).uri]);

  useEffect(() => {
    function handleMessage(ev: MessageEvent) {
      if (ev?.data && ev.data.__RN_WEBVIEW_BRIDGE) {
        onMessage?.({ nativeEvent: { data: ev.data.data } });
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onMessage]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      onLoadEnd?.();
      onNavigationStateChange?.({
        url: iframe?.contentWindow?.location?.href,
        title: (() => {
          try {
            return iframe?.contentDocument?.title ?? null;
          } catch {
            return null; // cross-origin
          }
        })(),
        loading: false,
        canGoBack: true,
        canGoForward: true,
      });
    };

    const handleError = (e: any) => onError?.(e);

    iframe.addEventListener("load", handleLoad);
    iframe.addEventListener("error", handleError);
    return () => {
      iframe.removeEventListener("load", handleLoad);
      iframe.removeEventListener("error", handleError);
    };
  }, [onLoadEnd, onNavigationStateChange, onError]);

  useImperativeHandle(ref, () => ({
    reload() {
      iframeRef.current?.contentWindow?.location?.reload();
    },
    goBack() {
      iframeRef.current?.contentWindow?.history?.back();
    },
    goForward() {
      iframeRef.current?.contentWindow?.history?.forward();
    },
    postMessage(data: any, targetOrigin = "*") {
      // Envia do app -> iframe (necessário script no conteúdo ler 'message')
      iframeRef.current?.contentWindow?.postMessage(
        { __RN_APP_TO_WEBVIEW: true, data },
        targetOrigin
      );
    },
    injectJavaScript(code: string) {
      // Só funciona se for mesmo-origin + com allow-scripts/sandbox adequados
      try {
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        // Tenta via eval no contexto do iframe (pode falhar cross-origin)
        (win as any).eval?.(code);
      } catch {
        // silencia para cross-origin
      }
    },
  }));

  // Monta atributos common
  const allow = ALLOW_ATTR_DEFAULT;
  const allowFullScreen = allowFullscreen;
  const sandbox = SANDBOX_DEFAULT;

  // Observação: headers customizados e POST não são suportados nativamente por <iframe>.
  // Se precisar POST, crie um srcDoc que faça um <form method="POST"> auto-submit para a URL desejada.

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, ...containerStyle }}>
      <iframe
        ref={iframeRef}
        title="RNWebView"
        src={isHtmlSource(source) ? undefined : (source as any).uri}
        srcDoc={srcDoc}
        allow={allow}
        allowFullScreen={allowFullScreen}
        sandbox={javaScriptEnabled ? sandbox : undefined}
        // importam para mídia inline em iOS Safari
        allowTransparency
        // estilização
        style={{
          border: "0",
          width: "100%",
          height: "100%",
          flex: 1,
          ...style,
        }}
        {...rest}
      />
    </div>
  );
});

export default WebView;
